import type { NetworkPlayer, NetworkPredictionState } from './protocol';

type RecordValue = Record<string, unknown>;
const record = (value: unknown): value is RecordValue =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const between = (value: unknown, minimum: number, maximum: number): value is number =>
  finite(value) && value >= minimum && value <= maximum;
const vector = (value: unknown): boolean => record(value) && finite(value.x) && finite(value.y) && finite(value.z);

function validInput(value: unknown): boolean {
  return record(value) && between(value.seq, 0, 0xffffffff) && Number.isInteger(value.seq) &&
    between(value.forward, -1, 1) && between(value.right, -1, 1) &&
    between(value.yaw, -2*Math.PI, 2*Math.PI) && between(value.pitch, -Math.PI/2.1, Math.PI/2.1) &&
    ['jump', 'grab', 'sprint', 'aiming', 'extracting'].every(key => typeof value[key] === 'boolean');
}

function validClimb(value: unknown): boolean {
  if (value === null) return true;
  if (!record(value) || (value.axis !== 'x' && value.axis !== 'z') || (value.edge !== -1 && value.edge !== 1)) return false;
  const surface = value.surface;
  return record(surface) && finite(surface.minimumX) && finite(surface.maximumX) &&
    finite(surface.minimumZ) && finite(surface.maximumZ) && finite(surface.height) &&
    surface.minimumX < surface.maximumX && surface.minimumZ < surface.maximumZ &&
    typeof surface.couplerConsole === 'boolean';
}

function validCable(value: unknown): boolean {
  return value === null || (record(value) && vector(value.target) &&
    between(value.remaining, Number.MIN_VALUE, 2.4));
}

/** Missing, future or incomplete capabilities must not become guessed replay state. */
export function decodePredictionState(value: unknown): NetworkPredictionState | null {
  if (!record(value) || value.version !== 1 || !validInput(value.input) ||
    typeof value.jumpHeld !== 'boolean' || typeof value.climbRearmRequired !== 'boolean' ||
    !between(value.jumpBuffer, 0, 0.14) || !between(value.coyoteRemaining, 0, 0.12) ||
    !between(value.climbPause, 0, 0.28) || !validClimb(value.climb) || !validCable(value.grapple)) return null;
  return value as unknown as NetworkPredictionState;
}

/** Checks that movement memory belongs to the containing authoritative pose. */
export function readPredictionState(player: NetworkPlayer): NetworkPredictionState | null {
  const state = decodePredictionState(player.predictionState);
  if (!state || state.input.seq !== player.lastInput || Boolean(player.climbing) !== (state.climb !== null) ||
    (player.grounded && state.climb !== null) || Boolean(player.gadgets?.grappleActive) !== (state.grapple !== null)) return null;
  if (state.grapple) {
    const target = player.gadgets?.grappleTarget;
    if (!target || target.x !== state.grapple.target.x || target.y !== state.grapple.target.y || target.z !== state.grapple.target.z) return null;
  }
  return state;
}
